import { useEffect, useState } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";

import { API_ROUTES } from "@/config/apiRoutes";
import { getPagePath, PAGES } from "@/config/pages";
import { isSupportedLanguage } from "@/i18n/routing";
import { useAuthStore } from "@/stores/AuthStore";
import { useOrganisationStore } from "@/stores/OrganisationStore";
import type { AuthUser } from "@/Utils/Auth/authSession";
import { loggerService, LogTag } from "@/Utils/LoggerService";
import appFetch from "@/Utils/Services/Fetch/appFetch";

interface MeResponse {
  user: AuthUser;
}

const RequireOrganisationRoute = () => {
  const { lang } = useParams<{ lang: string }>();
  const { organisation, setOrganisation } = useOrganisationStore();
  const { setUser } = useAuthStore();
  const [hydrating, setHydrating] = useState(!organisation);
  const [hydrated, setHydrated] = useState(false);

  const onboardingPath = isSupportedLanguage(lang)
    ? getPagePath("onboarding", lang)
    : PAGES.onboarding;

  useEffect(() => {
    if (organisation) {
      setHydrating(false);
      setHydrated(true);
      return;
    }

    const hydrate = async () => {
      try {
        const response = (await appFetch(
          API_ROUTES.users.me,
        )) as MeResponse | null;

        if (response?.user?.organisation) {
          setOrganisation(response.user.organisation);
          setUser({ ...response.user });

          loggerService.info(
            LogTag.STORE,
            "RequireOrganisationRoute: org hydrated from /me",
          );
        }
      } finally {
        setHydrating(false);
        setHydrated(true);
      }
    };

    hydrate();
  }, []);

  if (hydrating) {
    return null;
  }

  if (!organisation && hydrated) {
    return <Navigate to={onboardingPath} replace />;
  }

  return <Outlet />;
};

export default RequireOrganisationRoute;
